/**
 * API Client - Base fetch wrapper with authentication
 *
 * Provides consistent API calls with:
 * - Automatic auth header injection
 * - Error handling
 * - JSON parsing
 */

import { markMutationSuccess, reportMutationFailure } from '../connectivityService';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Get auth token from localStorage
 */
export const getAuthToken = () => localStorage.getItem('googleAuthToken');

/**
 * Get customer auth token from localStorage
 */
export const getCustomerToken = () => localStorage.getItem('customerToken');

/**
 * Build headers for authenticated requests
 */
export const getAuthHeaders = (isJson = true) => {
  const headers = {
    'Authorization': `Bearer ${getAuthToken()}`,
  };
  if (isJson) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

/**
 * Build headers for customer authenticated requests
 */
export const getCustomerAuthHeaders = () => ({
  'Authorization': `Bearer ${getCustomerToken()}`,
  'Content-Type': 'application/json',
});

/**
 * Base API request function
 *
 * @param {string} endpoint - API endpoint (e.g., '/programs')
 * @param {object} options - Fetch options
 * @returns {Promise<any>} - Parsed JSON response
 * @throws {Error} - With error message from API or status code
 */
export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE}/api${endpoint}`;
  const method = String(options.method || 'GET').toUpperCase();
  const isMutation = !['GET', 'HEAD', 'OPTIONS'].includes(method);
  if (isMutation && typeof navigator !== 'undefined' && navigator.onLine === false) {
    reportMutationFailure();
    throw new Error('You are offline. Reconnect to take action.');
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(options.body && !(options.body instanceof FormData)),
      ...options.headers,
    },
  });

  // Handle non-JSON responses (like 204 No Content)
  if (response.status === 204) {
    return null;
  }

  const data = await response.json().catch(() => ({}));

  if (isMutation && response.ok) markMutationSuccess();

  if (!response.ok) {
    const errorMessage = data.detail || data.message || `Request failed: ${response.status}`;
    throw new Error(errorMessage);
  }

  return data;
};

/**
 * GET request
 */
export const get = (endpoint, options = {}) =>
  apiRequest(endpoint, { method: 'GET', ...options });

/**
 * POST request with JSON body
 */
export const post = (endpoint, body, options = {}) =>
  apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
    ...options,
  });

/**
 * PUT request with JSON body
 */
export const put = (endpoint, body, options = {}) =>
  apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
    ...options,
  });

/**
 * PATCH request with JSON body
 */
export const patch = (endpoint, body, options = {}) =>
  apiRequest(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
    ...options,
  });

/**
 * DELETE request
 */
export const del = (endpoint, options = {}) =>
  apiRequest(endpoint, { method: 'DELETE', ...options });

/**
 * POST request with FormData (for file uploads)
 */
export const postFormData = async (endpoint, formData) => {
  const url = `${API_BASE}/api${endpoint}`;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    reportMutationFailure();
    throw new Error('You are offline. Reconnect to take action.');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      // Don't set Content-Type - browser will set it with boundary for FormData
    },
    body: formData,
  });

  const data = await response.json().catch(() => ({}));

  if (response.ok) markMutationSuccess();

  if (!response.ok) {
    throw new Error(data.detail || `Upload failed: ${response.status}`);
  }

  return data;
};

/**
 * Public API request (no auth required)
 */
export const publicGet = async (endpoint) => {
  const url = `${API_BASE}/api${endpoint}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
};

/**
 * Customer API request (with customer token)
 */
export const customerRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE}/api${endpoint}`;
  const method = String(options.method || 'GET').toUpperCase();
  const isMutation = !['GET', 'HEAD', 'OPTIONS'].includes(method);
  if (isMutation && typeof navigator !== 'undefined' && navigator.onLine === false) {
    reportMutationFailure();
    throw new Error('You are offline. Reconnect to take action.');
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...getCustomerAuthHeaders(),
      ...options.headers,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (isMutation && response.ok) markMutationSuccess();

  if (!response.ok) {
    throw new Error(data.detail || `Request failed: ${response.status}`);
  }

  return data;
};

export default {
  get,
  post,
  put,
  del,
  postFormData,
  publicGet,
  customerRequest,
  getAuthToken,
  getCustomerToken,
};
